import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { BosDurum, Buton, Iskelet, Kart } from "@koclukpanel/ui";
import { ApiHatasi, guvenlikGrafikleri, type GuvenlikSerisi, type GunKovasi } from "./api";

/**
 * GÜVENLİK GRAFİKLERİ — F-SEC-08
 *
 * ================================================================
 * KÜTÜPHANE YOK — VE BU BİR TERCİH
 * ================================================================
 * Panelde grafik kütüphanesi bulunmuyor ve bu ekran için bir tane
 * eklenmedi. Sebebi "hafif olsun" değil: çizilen şey günlük sayımdan
 * ibaret bir çubuk dizisi ve bir kütüphane bunun için erişilebilirliği
 * kendi kurallarına devretmemizi isterdi.
 *
 * Aşağıdaki çizim `<svg>` ile yapılıyor ama ekran okuyucuya **tablo**
 * olarak veriliyor: her günün tarihi ve sayısı okunabiliyor. Çoğu
 * grafik kütüphanesinin varsayılanı `aria-hidden` bir tuvaldir ve
 * o, görme engelli bir yöneticiye "burada bir şey var ama sana değil"
 * demektir.
 *
 * ================================================================
 * GRAFİK AÇIKLAMASIZ ÇİZİLMİYOR
 * ================================================================
 * Her serinin altında NEDEN bakıldığı yazıyor. Dalgalanan bir çizgi,
 * ne aradığını söylemezse herkese kendi korkusunu gösterir.
 */

/** Çubuğun yüksekliği (yüzde). Sıfır olanlar da GÖRÜNÜR kalıyor. */
export function cubukYuzdesi(sayi: number, enYuksek: number): number {
  if (enYuksek <= 0) return 0;
  const oran = (sayi / enYuksek) * 100;
  /*
    SIFIR OLMAYAN HER DEĞER EN AZ %4. Bir günde bir korumalı not
    erişimi, otuz erişimin olduğu bir ayda çizildiğinde %3,3 yükseklik
    alır ve ekranda GÖRÜNMEZ. Görünmeyen bir çubuk, olmayan bir olayla
    aynı şeydir — oysa aranan şey tam olarak o tek erişim olabilir.
  */
  if (sayi > 0 && oran < 4) return 4;
  return oran;
}

/** `2026-09-06` → `6 Eyl`. Eksende yalnızca birkaçı yazılıyor. */
export function kisaGun(gun: string): string {
  const t = new Date(`${gun}T00:00:00Z`);
  if (Number.isNaN(t.getTime())) return gun;
  return new Intl.DateTimeFormat("tr-TR", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(t);
}

/**
 * Eksende hangi günlerin yazılacağı.
 *
 * Otuz tarihi yan yana yazmak okunmuyor; ilk, orta ve son yeterli.
 * Sayı değil KONUM seçiliyor: seri kaç günlük olursa olsun üç etiket
 * çıkıyor ve aralarındaki mesafe kendiliğinden ölçekleniyor.
 */
export function eksenEtiketleri(kovalar: readonly GunKovasi[]): string[] {
  if (kovalar.length === 0) return [];
  if (kovalar.length <= 3) return kovalar.map((k) => kisaGun(k.gun));
  const orta = Math.floor(kovalar.length / 2);
  return [
    kisaGun(kovalar[0]!.gun),
    kisaGun(kovalar[orta]!.gun),
    kisaGun(kovalar[kovalar.length - 1]!.gun),
  ];
}

function Cubuklar({ seri }: { seri: GuvenlikSerisi }) {
  const etiketler = eksenEtiketleri(seri.kovalar);
  const baslikId = `grafik-${seri.anahtar}`;

  return (
    <div>
      <div
        className="bg-notr-50 rounded-alan flex h-24 items-end gap-px p-2"
        /*
          GÖRSEL KATMAN EKRAN OKUYUCUDAN GİZLİ, VERİ AŞAĞIDA TABLO
          OLARAK VERİLİYOR. Çubukları tek tek okutmak otuz anlamsız
          "grafik öğesi" duyurusu üretirdi; tablo ise gezinebilir.
        */
        aria-hidden="true"
      >
        {seri.kovalar.map((k) => (
          <div
            key={k.gun}
            className={`flex-1 rounded-t-[2px] ${
              k.sayi === 0 ? "bg-notr-200" : seri.yuksekIyiMi ? "bg-onay-600" : "bg-marka-600"
            }`}
            /*
              SIFIR GÜN 2 PİKSEL — 0 DEĞİL. Ölçüldü: `height: 0px`
              veren bir çubuk hiç çizilmiyor ve o gün grafikte bir
              BOŞLUK oluyor. Boşluk, "o gün yoktu" gibi okunuyor; oysa
              gün vardı ve içinde hiçbir şey olmadı — bu iki şey ayrı.

              2 piksel ile en küçük dolu çubuk (%4) arasında net bir
              fark var, üstelik renk de ayrı: gri sıfır, mavi dolu.
              Ayrım yalnızca renge bırakılmadı.
            */
            style={{
              height: k.sayi === 0 ? "2px" : `${cubukYuzdesi(k.sayi, seri.enYuksek)}%`,
            }}
            title={`${k.gun}: ${k.sayi}`}
          />
        ))}
      </div>

      <div className="text-notr-600 mt-1 flex justify-between text-xs" aria-hidden="true">
        {etiketler.map((e, i) => (
          <span key={`${e}-${i}`}>{e}</span>
        ))}
      </div>

      {/*
        EKRAN OKUYUCU İÇİN ASIL İÇERİK. Görsel olarak gizli ama DOM'da
        ve gezinilebilir; `display:none` olsaydı okunmazdı.
      */}
      <table className="sr-only">
        <caption id={baslikId}>{seri.baslik} — günlük sayım</caption>
        <thead>
          <tr>
            <th scope="col">Gün</th>
            <th scope="col">Sayı</th>
          </tr>
        </thead>
        <tbody>
          {seri.kovalar.map((k) => (
            <tr key={k.gun}>
              <th scope="row">{kisaGun(k.gun)}</th>
              <td>{k.sayi}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SeriKarti({ seri, gun }: { seri: GuvenlikSerisi; gun: number }) {
  return (
    <div className="border-notr-200 flex flex-col gap-2 border-b py-4 last:border-b-0">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-notr-900 text-sm font-medium">{seri.baslik}</p>
        <p className="text-notr-700 text-sm tabular-nums">
          son {gun} günde <span className="font-medium">{seri.toplam}</span>
        </p>
      </div>
      <Cubuklar seri={seri} />
      <p className="text-notr-600 text-xs">{seri.aciklama}</p>
    </div>
  );
}

export function GuvenlikGrafikleri() {
  const [gun, setGun] = useState(30);
  const veri = useQuery({
    queryKey: ["guvenlik-grafik", gun],
    queryFn: () => guvenlikGrafikleri(gun),
    retry: false,
  });

  if (veri.isPending) return <Iskelet satir={6} />;

  if (veri.isError) {
    return (
      <Kart baslik="Güvenlik grafikleri">
        <BosDurum
          ton="hata"
          baslik="Grafikler alınamadı"
          aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
          eylem={
            <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
              Tekrar dene
            </Buton>
          }
        />
      </Kart>
    );
  }

  const { seriler, anlik, saatDilimiNotu } = veri.data;

  return (
    <Kart
      baslik="Güvenlik grafikleri"
      aciklama="En üstteki seri bilerek korumalı not erişimi: diğerleri saldırganı arar, o içeriden yanlış kullanımı arar."
      eylem={
        <div className="flex gap-1">
          {[7, 30, 90].map((s) => (
            <Buton key={s} gorunum={s === gun ? "birincil" : "ikincil"} onClick={() => setGun(s)}>
              {s} gün
            </Buton>
          ))}
        </div>
      }
    >
      <dl className="border-notr-200 grid grid-cols-2 gap-3 border-b pb-4 text-sm sm:grid-cols-3">
        <div>
          <dt className="text-notr-600">Personelde 2FA</dt>
          <dd className="tabular-nums">
            {anlik.ikiAsamaOrani === null ? (
              /*
                `null` = SAYILACAK PERSONEL YOK. "%0" yazmak yanlış
                alarm olurdu: biri "kimse kurmamış", öteki "kimse yok".
              */
              <span className="text-notr-600">personel kaydı yok</span>
            ) : (
              <span className={anlik.ikiAsamaOrani < 100 ? "text-uyari-800 font-medium" : ""}>
                %{anlik.ikiAsamaOrani}{" "}
                <span className="text-notr-600 text-xs font-normal">
                  ({anlik.ikiAsamaliPersonel}/{anlik.personelToplam})
                </span>
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-notr-600">Açık oturum</dt>
          <dd className="text-notr-900 tabular-nums">{anlik.acikOturum}</dd>
        </div>
      </dl>

      <div className="flex flex-col">
        {seriler.map((s) => (
          <SeriKarti key={s.anahtar} seri={s} gun={gun} />
        ))}
      </div>

      {/*
        SAAT DİLİMİ NOTU GİZLENMİYOR. Grafiğin gün sınırı UTC ve
        Türkiye'de gece 03:00 öncesi olaylar bir önceki güne düşüyor —
        gece yoğunluğu tam da bakılan şey olduğu için bu okunmadan
        bakılmamalı.
      */}
      <p className="text-notr-600 mt-3 text-xs">{saatDilimiNotu}</p>
    </Kart>
  );
}
